/**
 * Thai catalog — the SOURCE OF TRUTH for message keys (I18N-001/I18N-002).
 *
 * Thai is the primary locale (docs/requirements/21-localization.md §21.1) and
 * these strings are the exact copy that shipped before the i18n retrofit —
 * do NOT reword them casually; consent/safety wording is reviewed copy
 * (no machine translation of legal/consent/safety strings).
 *
 * Keys are namespaced by surface/feature (I18N-015). Interpolation uses
 * `{placeholder}` tokens resolved by `t()` in `./index.ts` (I18N-007 — no
 * string concatenation to build sentences).
 *
 * The Tonight Mode vocabulary under `mode.*` is LOCKED (I18N-006 /
 * CLAUDE.md "Tonight Mode Vocabulary") — never paraphrase it.
 */
export const th = {
  // Brand — identical in every locale (the Thai product name IS the brand).
  "brand.appName": "วันนี้เบาๆนะ",

  // Locale switcher labels — each language names itself, never translated.
  "locale.th": "ไทย",
  "locale.en": "EN",
  "settings.language": "ภาษา",

  // Common / shared
  "common.you": "คุณ",
  "common.back": "กลับ",
  "common.backHome": "กลับหน้าหลัก",
  "common.notYet": "ยังก่อน",
  "common.table": "โต๊ะ {code}",
  "common.toTable": "ถึงโต๊ะ {table}",
  "common.saving": "กำลังบันทึก...",
  "common.itemsCount": "{count} รายการ",
  "common.cantSendYet": "ยังส่งไม่ได้",
  "common.block": "บล็อก",
  "common.report": "รายงาน",
  "common.profileTonight": "โปรไฟล์คืนนี้",

  // Root LIFF gate
  "root.connecting": "กำลังเชื่อมต่อ…",
  "root.lineConnectFailed": "เชื่อมต่อ LINE ไม่สำเร็จ",
  "root.openInLineTitle": "เปิดผ่านแอป LINE",
  "root.openInLineDesc": "กรุณาเปิดลิงก์นี้ในแอป LINE เพื่อเริ่มใช้งาน Just One Drink",
  "root.loginWithLine": "เข้าสู่ระบบด้วย LINE",

  // Connectivity banner (FR-OFF-002)
  "connectivity.offline": "ออฟไลน์อยู่ · ข้อมูลอาจไม่อัปเดต การส่งจะทำอัตโนมัติเมื่อกลับมาออนไลน์",
  "connectivity.degraded": "เครือข่ายช้าอยู่ตอนนี้ · ข้อมูลอาจไม่ใหม่ล่าสุด กำลังลองใหม่อัตโนมัติ",

  // Bottom navigation
  "nav.home": "หน้าหลัก",
  "nav.order": "สั่งของ",
  "nav.around": "รอบร้าน",
  "nav.inbox": "กล่อง",
  "nav.me": "ฉัน",

  // Welcome
  "welcome.tonightAt": "คืนนี้ที่ {venue} · {session}",
  "welcome.start": "เริ่มเบาๆ",
  "welcome.orderOnly": "สั่งของอย่างเดียว",

  // Age gate + consent (reviewed safety copy — do not reword)
  "ageGate.kicker": "ก่อนเริ่มคืนนี้",
  "ageGate.title": "ดื่มเบาๆ · คุณคุมเอง",
  "ageGate.confirmAge": "ฉันยืนยันว่ามีอายุ 20 ปีขึ้นไป",
  "ageGate.confirmConsent": "ยอมรับเงื่อนไขความเป็นส่วนตัวและความยินยอม ({version})",
  "ageGate.blockedNote": "ขอบคุณที่เข้ามา · บริการนี้สำหรับผู้ที่มีอายุ 20 ปีขึ้นไปเท่านั้น",
  "ageGate.confirm": "ยืนยันและเข้าใช้งาน",
  "ageGate.under20": "ยังไม่ถึง 20",

  // LINE profile onboarding
  "lineProfile.changePhotoAria": "เปลี่ยนรูปโปรไฟล์",
  "lineProfile.changePhoto": "เปลี่ยนรูป",
  "lineProfile.useLinePhoto": "ใช้รูป LINE",
  "lineProfile.useGlyph": "หรือใช้ไอคอนแทนรูป",
  "lineProfile.namePlaceholder": "ชื่อที่อยากให้คนอื่นเห็น",
  "lineProfile.privacyNote": "รูปและชื่อนี้เห็นเฉพาะคุณ จนกว่าจะเปิดโหมดให้คนอื่นเห็น",
  "lineProfile.useProfile": "ใช้โปรไฟล์นี้",

  // Join table / check-in
  "joinTable.kicker": "เช็คอิน",
  "joinTable.orderOnlyTitle": "สั่งแบบไม่นั่งโต๊ะ",
  "joinTable.confirmTitle": "นี่โต๊ะของคุณ",
  "joinTable.tokenInvalid": "QR token นี้หมดอายุหรือไม่ตรงกับ session คืนนี้ · เลือกโต๊ะด้านล่างหรือสแกนใหม่ที่โต๊ะ",
  "joinTable.branch": "สาขา",
  "joinTable.tableKicker": "โต๊ะ",
  "joinTable.friendsCheckedIn": "เพื่อนเช็คอินแล้ว {count} คน",
  "joinTable.pickHint": "เลือกโต๊ะของคุณ หรือสแกน QR ที่โต๊ะอีกครั้ง",
  "joinTable.noTableChip": "ไม่มีโต๊ะ",
  "joinTable.enterOrderOnly": "เข้าแบบสั่งอย่างเดียว",
  "joinTable.enterTable": "เข้าโต๊ะนี้",
  "joinTable.enterConfirm": "ใช่ เข้าโต๊ะนี้",
  "joinTable.notMyTable": "ไม่ใช่โต๊ะนี้?",

  // Tonight-mode picker screen
  "tonightMode.kicker": "โหมดคืนนี้",
  "tonightMode.title": "เปิดตัวแค่ไหนดี?",
  "tonightMode.previewHiddenKicker": "ตอนนี้ในห้อง",
  "tonightMode.previewVisibleKicker": "โต๊ะอื่นจะเห็นคุณแบบนี้",
  "tonightMode.hidden": "ซ่อนตัวอยู่",
  "tonightMode.hiddenSub": "ไม่มีใครเห็นโต๊ะคุณ",
  "tonightMode.tableAndMode": "โต๊ะ {table} · {mode}",
  "tonightMode.enter": "เข้าร้านเลย",

  // Tonight Mode vocabulary — LOCKED (I18N-006, CLAUDE.md)
  "mode.private.title": "ไม่เปิดตัว",
  "mode.private.desc": "ไม่โชว์ตัว · แค่สั่งของก็พอ",
  "mode.private.preview": "ซ่อนอยู่ · ไม่มีใครเห็นโต๊ะคุณ",
  "mode.soft_open.title": "เปิดตัวนิดนึง",
  "mode.soft_open.desc": "เห็นชื่อ/รูป แต่ต้องขอก่อนคุย",
  "mode.soft_open.preview": "เห็นชื่อคุณ · ทักได้แต่ต้องขอก่อน",
  "mode.with_friends.title": "มากับเพื่อน",
  "mode.with_friends.desc": "เปิดแบบกลุ่ม มากันหลายคน",
  "mode.with_friends.preview": "เปิดเป็นกลุ่ม · เพื่อนๆ มาด้วยกัน",
  "mode.lets_cheers.title": "ชนแก้วได้เลย",
  "mode.lets_cheers.desc": "เปิดรับชนแก้ว · ส่งดื่มหากันได้",
  "mode.lets_cheers.preview": "เปิดรับชนแก้ว & ส่งดื่มหากัน",

  // Home tab
  "home.loadingVenue": "กำลังโหลดร้าน...",
  "home.sendDrinkTitle": "ส่ง Just One Drink",
  "home.sendDrinkSub": "ส่งให้โต๊ะอื่นแบบเบาๆ · เขากดรับก่อนถึงเสิร์ฟ",
  "home.tileMenu": "เมนู",
  "home.tileFloor": "ผังร้าน",
  "home.tileFloorSub": "ดูโต๊ะรอบๆ",
  "home.tileGames": "เล่นเบาๆ",
  "home.tileScreen": "ขึ้นจอ",
  "home.tileScreenSub": "โพสต์บนจอร้าน",
  "home.nowKicker": "ตอนนี้",
  "home.ourTable": "โต๊ะเรา",
  "home.orderRow": "ออเดอร์โต๊ะคุณ",
  "home.orderInProgress": "กำลังดำเนินการ",
  "home.requestsPending": "{count} รอคุณตอบ",
  "home.requestsNone": "ไม่มีค้าง",
  "home.reorder": "สั่งซ้ำจากรอบก่อน",

  // Menu tab
  "menu.kicker": "เมนูคืนนี้",
  "menu.title": "บาร์ & ครัว",
  "menu.loading": "กำลังโหลดเมนูจาก API...",
  "menu.loadError": "โหลดเมนูจาก API ไม่สำเร็จ — ไม่ใช้ mock fallback ใน backend mode",
  "menu.alcoholBadge": "แอลกอฮอล์ 18+",
  "menu.soldOutBadge": "หมดแล้ว",
  "menu.soldOut": "หมด",
  "menu.add": "เพิ่ม",
  "menu.addedToast": "ใส่ {item} ลงตะกร้าแล้ว",
  "menu.viewCart": "ดูตะกร้า",

  // Cart
  "cart.backToMenu": "กลับไปที่เมนู",
  "cart.title": "ตะกร้าโต๊ะเรา",
  "cart.emptyTitle": "ยังไม่มีอะไรในตะกร้า",
  "cart.emptyDesc": "เลือกเครื่องดื่มหรืออาหารจากเมนูได้เลย",
  "cart.viewMenu": "ดูเมนู",
  "cart.qtyAria": "จำนวน {item}",
  "cart.total": "รวมทั้งหมด",
  "cart.payAtVenue": "ชำระที่ร้าน · จ่ายภายหลังได้",
  "cart.submit": "ส่งออเดอร์ไปที่โต๊ะ",

  // Live order status (locked Order state machine display labels)
  "order.step.pending": "ส่งออเดอร์แล้ว",
  "order.step.accepted": "ร้านรับออเดอร์",
  "order.step.preparing": "กำลังจัดเตรียม",
  "order.step.ready": "พร้อมเสิร์ฟ",
  "order.step.served": "เสิร์ฟแล้ว",
  "order.cancelledTitle": "ออเดอร์ถูกยกเลิก",
  "order.cancelledBody": "mock นี้เก็บเหตุผลไว้ให้ backend ผูก audit/refund ภายหลัง",
  "order.rejectedTitle": "ร้านปฏิเสธออเดอร์นี้",
  "order.rejectedBody": "อาจเป็นเพราะของหมดหรือหมดเวลาครัว · ไม่มีการคิดเงินรายการนี้",
  "order.servedBody": "รายการนี้เสร็จสมบูรณ์แล้ว",
  "order.none": "ยังไม่มีออเดอร์",
  "order.goToMenu": "ไปที่เมนู",
  "order.title": "ออเดอร์ของคุณ",
  "order.reason": "เหตุผล: {reason}",
  "order.activeBackend": "ร้านกำลังดูแลออเดอร์นี้อยู่",
  "order.activeMock": "mock staff/status event กำลังขยับรายการนี้",
  "order.total": "รวม",
  "order.statusKicker": "สถานะจากร้าน",
  "order.statusAuto": "อัปเดตอัตโนมัติทุก 5 วินาทีจาก Staff Ops · ไม่ต้องรีเฟรชเอง",
  "order.mockHint": "ใช้จำลอง event จาก Staff Ops ก่อน backend จริง",
  "order.viewAround": "ดูรอบร้านระหว่างรอ",

  // Around tab (consent-filtered discovery)
  "around.floorPlanRow": "ดูผังร้านคืนนี้",
  "around.openMap": "เปิดแผนที่ →",
  "around.youHidden": "คืนนี้คุณซ่อนตัวอยู่",
  "around.youOpen": "คืนนี้คุณเปิด · {mode}",
  "around.visibilityNote": "โต๊ะอื่นเห็นคุณเท่าที่คุณอนุญาต",
  "around.change": "เปลี่ยน",
  "around.kicker": "รอบร้าน",
  "around.title": "ดูรอบๆ",
  "around.openCount": "{count} โต๊ะเปิด",
  "around.loading": "กำลังโหลดโต๊ะจาก API",
  "around.emptyTitle": "ห้องยังเงียบอยู่เลย",
  "around.loadError": "โหลดโต๊ะจาก API ไม่สำเร็จ — ไม่ใช้ mock fallback ใน backend mode",
  "around.emptyBody": "ยังไม่มีโต๊ะที่เปิดตัวคืนนี้ ลองเช็คอีกทีสักครู่",
  "around.acceptingGifts": "เปิดรับส่งดื่ม",
  "around.openTonight": "เปิดตัวคืนนี้",
  "around.playing": "เล่นเกม",

  // Inbox (gifts + contact) — every social item is consent-gated
  "inbox.loadingTitle": "กำลังโหลดกล่องของคุณ",
  "inbox.emptyTitle": "กล่องยังว่างอยู่",
  "inbox.loadError": "โหลดของขวัญจาก API ไม่สำเร็จ — ลองใหม่อีกครั้ง",
  "inbox.emptyBody": "เมื่อมีคนส่ง Just One Drink หรือขอทักทาย จะเด้งมาที่นี่",
  "inbox.kicker": "กล่องของคุณ",
  "inbox.title": "คำขอ & ของขวัญ",
  "inbox.subtitle": "รับก่อนเสิร์ฟ · ปฏิเสธ/บล็อกได้เสมอ",
  "inbox.unknownTable": "ปริศนา",
  "inbox.sentToYou": "โต๊ะ {table} ส่งให้คุณ",
  "inbox.giftAcceptNote": "ถ้ารับ ร้านจะนำมาเสิร์ฟให้โต๊ะคุณหลัง staff fulfil",
  "inbox.igAttached": "แนบคำขอ IG มาด้วย",
  "inbox.acceptGift": "รับไว้ ขอบคุณ",
  "inbox.acceptGiftToast": "รับไว้แล้ว · รอ staff fulfil",
  "inbox.declineSoft": "ปฏิเสธนุ่มๆ",
  "inbox.declinedToast": "ปฏิเสธนุ่มๆ แล้ว",
  "inbox.blockToast": "บล็อกแล้ว · จะไม่เห็นคำขอจากโต๊ะนี้อีก",
  "inbox.reportToast": "รายงานแล้ว · ทีมงานจะตรวจสอบ",
  "inbox.greetsYou": "โต๊ะ {table} ขอทักทาย",
  "inbox.requestSubject": "ขอ {subject}",
  "inbox.contactNote": "เขาจะเห็นเมื่อคุณตอบรับเท่านั้น",
  "inbox.acceptedAwaitStaff": "รับแล้ว · รอพนักงานจัดการ",
  "inbox.acceptContact": "รับคำขอ",
  "inbox.acceptContactToast": "รับคำขอแล้ว",
  "inbox.fulfillToast": "staff mock fulfil แล้ว · พร้อมเสิร์ฟ",
  "inbox.staffRejectToast": "staff reject แล้ว · mock refund/void ถูกบันทึก",
  "inbox.finalStaffRejected": "พนักงานปฏิเสธ · refund/void mock",
  "inbox.finalDeclined": "ปฏิเสธแล้ว",
  "inbox.finalExpired": "หมดเวลาแล้ว",

  // Contact requests screen
  "requests.kicker": "คำขอทักทาย",
  "requests.subtitle": "ทุกคำขอต้องรอ accept ก่อนเปิดเผยคอนแทค",
  "requests.loading": "กำลังโหลดคำขอ",
  "requests.emptyTitle": "ยังไม่มีคำขอ",
  "requests.emptyBody": "คำขอทักทายที่ต้องการความยินยอมจากคุณจะแสดงที่นี่",
  "requests.viewInbox": "ดูกล่องของคุณ",
  "requests.awaitingYou": "รอคุณตอบ",
  "requests.noIncoming": "ไม่มีคำขอขาเข้า",
  "requests.sentSection": "ที่คุณส่งไป",
  "requests.noSent": "ยังไม่มีคำขอที่คุณส่ง",
  "requests.acceptToastConsent": "รับคำขอแล้ว · เปิดเผยเฉพาะช่องทางที่คุณยินยอม",
  "requests.rejectToast": "ปฏิเสธคำขอแล้ว",
  "requests.consentNote": "จะเห็นคอนแทคได้หลังคุณรับเท่านั้น",
  "requests.acceptedConsent": "รับแล้ว · เปิดเผยเฉพาะช่องทางที่คุณยินยอม",
  "requests.reject": "ปฏิเสธ",
  "requests.presetFallback": "ทักเบาๆ",
  "requests.asksFor": "โต๊ะ {table} ขอ {subject}",
  "requests.acceptFromToast": "รับคำขอจากโต๊ะ {table} แล้ว",
  "requests.blockTableToast": "บล็อกโต๊ะ {table} แล้ว",

  // Me tab
  "me.leaveBlockedFallback": "ยังออกจากร้านไม่ได้",
  "me.deleteBackendOnly": "ลบข้อมูลได้เฉพาะโหมด backend เท่านั้น",
  "me.deletedToast": "ลบข้อมูลโปรไฟล์ของคุณแล้ว",
  "me.deleteFailedToast": "ลบข้อมูลไม่สำเร็จ ลองใหม่อีกครั้ง",
  "me.lastOrder": "ออเดอร์ล่าสุด: {status}",
  "me.editProfile": "แก้โปรไฟล์ของฉัน",
  "me.adjustMode": "ปรับโหมดคืนนี้",
  "me.allowGifts": "รับ Just One Drink",
  "me.allowGiftsSub": "ให้โต๊ะอื่นส่งดื่มให้คุณได้",
  "me.giftsOffToast": "ปิดรับดื่มแล้ว",
  "me.giftsOnToast": "เปิดรับดื่มแล้ว",
  "me.allowContact": "รับคำขอทักทาย",
  "me.allowContactSub": "ให้คนอื่นขอคอนแทคได้ (คุณเลือกตอบเอง)",
  "me.contactOffToast": "ปิดรับคำขอทักทายแล้ว",
  "me.contactOnToast": "เปิดรับคำขอทักทายแล้ว",
  "me.safetyKicker": "ความปลอดภัยคืนนี้",
  "me.none": "ไม่มี",
  "me.reports": "รายงานปัญหา",
  "me.noneYet": "ยังไม่มี",
  "me.pendingConsent": "คำขอที่กำลังรอ consent",
  "me.sentOut": "{count} ส่งออก",
  "me.leave": "ออกจากร้าน / ปิดหน้านี้",
  "me.deleteMyData": "ลบข้อมูลของฉัน",
  "me.leaveConfirmTitle": "ออกจากร้านคืนนี้?",
  "me.leaveConfirmBusy": "ยังมีออเดอร์ที่ staff กำลังดูแลอยู่ mock นี้จะกันไม่ให้ออกจากร้านจนปิดรายการ",
  "me.leaveConfirmBody": "ระบบจะเคลียร์ตะกร้า คำขอขาออก และกลับเป็นโหมด private",
  "me.leaveConfirm": "ออกจากร้าน",
  "me.deleteConfirmTitle": "ลบข้อมูลโปรไฟล์ของฉัน?",
  "me.deleteConfirmBody":
    "ชื่อที่แสดง, bio, สายที่เลือก, และ Instagram ที่คุณเคยกรอกจะถูกลบทันทีและกู้คืนไม่ได้ คุณยังอยู่ในร้านและสั่งของต่อได้ตามปกติ",
  "me.deleteConfirm": "ลบข้อมูลถาวร",

  // Blocked tables (FR-CUS-124)
  "blocked.kicker": "ความปลอดภัย",
  "blocked.title": "โต๊ะที่บล็อกไว้",
  "blocked.subtitle": "คนที่ถูกบล็อกจะส่งดื่มหรือขอทักทายคุณไม่ได้ · เลิกบล็อกได้ทุกเมื่อ",
  "blocked.loading": "กำลังโหลด",
  "blocked.emptyTitle": "ยังไม่มีใครถูกบล็อก",
  "blocked.emptyBody": "เมื่อคุณบล็อกโต๊ะไหน จะมาแสดงที่นี่",
  "blocked.unblock": "เลิกบล็อก",
  "blocked.unblockedToast": "เลิกบล็อก {name} แล้ว",
  "blocked.unblockFailed": "เลิกบล็อกไม่สำเร็จ ลองใหม่อีกครั้ง",
  "blocked.confirmTitle": "เลิกบล็อก {name}?",
  "blocked.confirmTableTitle": "เลิกบล็อกโต๊ะ {code}?",
  "blocked.confirmBody": "โต๊ะนี้จะส่งดื่มหรือขอทักทายคุณได้อีกครั้ง",
  "blocked.unblockedTableToast": "เลิกบล็อกโต๊ะ {code} แล้ว",

  // Profile editor
  "profile.kicker": "โปรไฟล์ของฉัน",
  "profile.saveFailed": "บันทึกโปรไฟล์ไม่สำเร็จ ลองใหม่อีกครั้ง",
  "profile.savedToast": "บันทึกโปรไฟล์คืนนี้แล้ว",
  "profile.galleryTitle": "แกลเลอรี",
  "profile.galleryHint": "รูปที่อยากให้คนที่คุณรับเป็นเพื่อนคืนนี้เห็น",
  "profile.removePhotoAria": "ลบรูป",
  "profile.introTitle": "แนะนำตัวสั้นๆ",
  "profile.statusPlaceholder": "สเตตัสคืนนี้ เช่น มาฟังเพลงชิลๆ",
  "profile.bioPlaceholder": "เล่าเกี่ยวกับคุณนิดหน่อย (ไม่บังคับ)",
  "profile.vibeTitle": "คืนนี้สายไหน",
  "profile.socialTitle": "โซเชียล",
  "profile.socialHint": "เผยเฉพาะหลังคุณกับอีกฝ่ายตอบรับกัน · ไม่โชว์ใน feed",
  "profile.igPlaceholder": "instagram (เช่น @yourname)",
  "profile.exposureTitle": "ใครเห็นอะไร",
  "profile.exposureHint": "คุมเองว่าเปิดให้คนรอบๆ เห็นแค่ไหน",
  "profile.tierHidden": "ตอนซ่อนตัว",
  "profile.tierHiddenValue": "ไม่มีใครเห็นคุณเลย",
  "profile.whenOpen": "ตอนเปิดตัว · คนรอบๆ เห็นได้",
  "profile.exposureBase": "ชื่อ + รูปโปรไฟล์",
  "profile.exposureBaseSub": "ชั้นพื้นฐาน ปิดไม่ได้ตอนเปิดตัว",
  "profile.exposureFirstPhoto": "รูปแรกในแกลเลอรี",
  "profile.exposureVibe": "สายไหน (vibe)",
  "profile.exposureBio": "แนะนำตัวสั้นๆ (bio)",
  "profile.save": "บันทึกโปรไฟล์",

  // Table detail
  "table.loading": "กำลังโหลดข้อมูลโต๊ะ…",
  "table.notFound": "ไม่พบโต๊ะนี้",
  "table.backToAround": "กลับไปดูรอบร้าน",
  "table.peopleCount": "{count} คน",
  "table.bioLine": "{label} · {count} คน",
  "table.giftsClosed": "โต๊ะนี้ปิดรับการส่งดื่มอยู่ตอนนี้",
  "table.contactClosed": "โต๊ะนี้ปิดรับคำขอทักทายอยู่ตอนนี้",
  "table.askIgMessage": "ขอ IG ได้ไหม",
  "table.softGreetMessage": "ทักเบาๆ แบบต้องรอรับก่อน",
  "table.requestSentToast": "ส่งคำขอ {subject} ถึงโต๊ะ {table} แล้ว · เขาเลือกตอบเอง",
  "table.consentShareNote": "รูปเพิ่มเติมและ IG จะแชร์ให้กันเองก็ต่อเมื่อรับคำขอทักทายแล้วเท่านั้น",
  "table.giftGuardPrefix": "ส่งดื่ม: {reason}",
  "table.contactGuardPrefix": "ทักทาย: {reason}",
  "table.askIg": "ขอ IG",
  "table.softGreet": "ส่งคำทักเบาๆ",
  "table.blockedToast": "บล็อกโต๊ะนี้แล้ว",
  "table.reportToast": "ส่งรายงานแล้ว · staff moderation จะเห็นรายการนี้",
  "table.statusOpenOrders": "เปิดรับออเดอร์",
  "table.statusInUse": "กำลังใช้งาน",

  // Room / floor plan
  "room.loadingFloor": "กำลังโหลดผังร้าน…",
  "room.floorTonight": "ผังร้านคืนนี้",
  "room.yourTablePrefix": "โต๊ะของคุณคือ",
  "room.tapOtherToSend": "แตะโต๊ะอื่นเพื่อส่งเครื่องดื่ม",
  "room.tapToSend": "แตะโต๊ะเพื่อส่งเครื่องดื่ม",
  "room.sheetDesc": "ส่งเครื่องดื่มแบบไม่เปิดตัว · รับหรือไม่รับขึ้นกับเขา",
  "room.sendDrink": "ส่งเครื่องดื่ม",
  "room.reportToast": "รายงานแล้ว ทีมงานจะตรวจสอบ",

  // Send-drink (gift) flow
  "send.noTarget": "ไม่พบโต๊ะปลายทาง",
  "send.sealedToast": "ผนึกส่งให้โต๊ะ {table} แล้ว",
  "send.fromYou": "โต๊ะ {table} (คุณ)",
  "send.sentNote": "ส่งแล้ว · เขาต้องกดรับก่อน ร้านถึงจะเสิร์ฟ",
  "send.mockStatus": "สถานะ mock: {status}",
  "send.igAttachedShort": "แนบคำขอ IG แล้ว",
  "send.kicker": "ส่งให้เบาๆ",
  "send.selectedFallback": "ที่เลือก",
  "send.currentMode": "โหมดคุณตอนนี้: {mode}",
  "send.modeGateNote": "โหมด private หรือปิดรับคอนแทคจะกันการส่งคำขอที่ไม่ผ่าน consent",
  "send.chooseItem": "เลือกของ",
  "send.loadingGifts": "กำลังโหลดของขวัญจาก API...",
  "send.loadError": "โหลดข้อมูลจาก API ไม่สำเร็จ — ไม่ใช้ mock fallback ใน backend mode",
  "send.attachMessage": "แนบข้อความ",
  "send.attachIg": "แนบคำขอ IG (เขาเลือกรับหรือปฏิเสธได้)",
  "send.contactOffWarning": "คุณปิดรับคอนแทคอยู่ — เปิดก่อนแนบ IG",
  "send.sealAndSend": "ผนึกแล้วส่งเลย",
  "send.confirmTitle": "ส่งเลยไหม?",
  "send.confirmDesc": "เขาต้องกดรับก่อน ร้านถึงจะเสิร์ฟ {item} ให้โต๊ะ {table}",
  "send.confirmSeal": "ผนึกส่ง",

  // Games (coming soon)
  "games.kicker": "ระหว่างรอ",
  "games.title": "เล่นอะไรเบาๆ",
  "games.soon": "เร็วๆ นี้",
  "games.soonBody": "ฟีเจอร์เกมและกิจกรรมระหว่างรอกำลังอยู่ระหว่างพัฒนา ไม่มีเดิมพัน ไม่มีเงินรางวัล",

  // Screen (pay-to-post) flow
  "screen.guardTitle": "ขึ้นจอใหญ่",
  "screen.titleBig": "ขึ้นจอใหญ่ 📺",
  "screen.checkInFirst": "เช็คอินที่โต๊ะก่อนถึงจะใช้งานได้",
  "screen.kicker": "โพสต์บนจอใหญ่",
  "screen.pickHint": "เลือกประเภทโพสต์ · ชำระเงินกับพนักงานเพื่อขึ้นจอ",
  "screen.type.messageDesc": "ส่งข้อความขึ้นจอ",
  "screen.type.warpDesc": "แจก IG / โชว์รูป",
  "screen.type.songDesc": "ขอเพลงจาก DJ",
  "screen.type.tipDesc": "ทิปให้ศิลปิน / สตาฟ",
  "screen.next": "ถัดไป →",
  "screen.messageLabel": "ข้อความ (สูงสุด 120 ตัวอักษร) *",
  "screen.messagePlaceholder": "พิมพ์ข้อความที่อยากให้ขึ้นจอ...",
  "screen.igLabel": "IG Handle (@ ไม่ต้องใส่ก็ได้)",
  "screen.photoLabel": "รูปภาพ (ไม่บังคับ)",
  "screen.uploading": "กำลังอัปโหลด...",
  "screen.uploaded": "อัปโหลดแล้ว ✓",
  "screen.choosePhoto": "เลือกรูป (JPEG/PNG)",
  "screen.captionLabel": "ข้อความประกอบ (ไม่บังคับ)",
  "screen.captionPlaceholder": "ข้อความสั้นๆ...",
  "screen.songLabel": "ชื่อเพลงที่ขอ *",
  "screen.songPlaceholder": "ชื่อเพลง — ศิลปิน",
  "screen.tipTargetLabel": "ผู้รับทิป *",
  "screen.loadingShort": "กำลังโหลด...",
  "screen.noTipTargets": "ไม่มีผู้รับทิปในขณะนี้",
  "screen.tipAmountLabel": "จำนวนทิป (บาท) · ขั้นต่ำ {min}",
  "screen.uploadFailedWith": "อัปโหลดไม่สำเร็จ: {error}",
  "screen.uploadFailed": "อัปโหลดไม่สำเร็จ",
  "screen.postFailed": "โพสต์ไม่สำเร็จ",
  "screen.mockNote": "Mock mode — โพสต์จะไม่ถูกส่งไปยัง backend จริง",
  "screen.submitting": "กำลังส่ง...",
  "screen.submit": "โพสต์เลย 📺",
  "screen.successTitle": "โพสต์แล้ว!",
  "screen.successBody": "โพสต์แล้ว! ไปจ่ายเงินกับพนักงานเพื่อให้ข้อความขึ้นจอ",
  "screen.typeLine": "ประเภท: {type}",
  "screen.postAgain": "โพสต์อีกครั้ง",
  "screen.myPosts": "โพสต์ของฉัน",
  "screen.pdpaNote": "ข้อความนี้จะแสดงบนจอใหญ่ของร้านที่ทุกคนมองเห็นได้ · ชื่อที่แสดงคือชื่อที่คุณเช็คอิน",
  "screen.showAvatar": "แสดง LINE Avatar บนจอ",
  "screen.avatarLockedNote": "เปิดอัตโนมัติสำหรับ แจกวาร์ป",
  "screen.showTable": "แสดงเลขโต๊ะบนจอ",

  // Screen domain validation + status labels
  "screen.validation.messageRequired": "กรุณาใส่ข้อความ",
  "screen.validation.messageTooLong": "ข้อความยาวเกิน 120 ตัวอักษร",
  "screen.validation.warpNeedsIgOrPhoto": "ใส่ IG handle หรืออัปโหลดรูปอย่างน้อยหนึ่งอย่าง",
  "screen.validation.igTooLong": "IG handle ยาวเกิน 60 ตัวอักษร",
  "screen.validation.songRequired": "กรุณาใส่ชื่อเพลง",
  "screen.validation.songTooLong": "ชื่อเพลงยาวเกิน 120 ตัวอักษร",
  "screen.validation.tipTargetRequired": "กรุณาเลือกผู้รับทิป",
  "screen.validation.tipAmountRequired": "กรุณาใส่จำนวนเงินทิป",
  "screen.status.submitted": "รอชำระเงินกับพนักงาน",
  "screen.status.paid": "รออนุมัติ",
  "screen.status.approved": "กำลังจะขึ้นจอ ✨",
  "screen.status.displayed": "ขึ้นจอแล้ว 🎉",
  "screen.status.rejected": "ไม่ผ่าน",
  "screen.status.removed": "ถูกนำออก",
  "screen.status.expired": "หมดอายุ",
  "screen.postType.message": "ส่งข้อความ",
  "screen.postType.warp": "แจกวาร์ป",
  "screen.postType.song_request": "ขอเพลง",
  "screen.postType.tip": "แจกทิป",

  // Session-level toasts + consent guards
  "session.checkInFailed": "เช็คอินไม่สำเร็จ",
  "session.orderRetrying": "เครือข่ายขัดข้อง · จะลองส่งออเดอร์ใหม่อัตโนมัติ",
  "session.giftRetrying": "เครือข่ายขัดข้อง · จะลองส่งดื่มใหม่อัตโนมัติ",
  "session.modeChangeFailed": "เปลี่ยนโหมดไม่สำเร็จ",
  "session.settingsSaveFailed": "บันทึกการตั้งค่าไม่สำเร็จ",
  "session.giftSendFailed": "ส่งดื่มไม่สำเร็จ",
  "session.contactSendFailed": "ส่งคำขอทักทายไม่สำเร็จ",
  "session.blockFailed": "บล็อกไม่สำเร็จ",
  "session.reportFailed": "ส่งรายงานไม่สำเร็จ",
  "session.giftRespondFailed": "ตอบกลับของขวัญไม่สำเร็จ",
  "session.contactRespondFailed": "ตอบกลับคำขอทักทายไม่สำเร็จ",
  "session.leaveFailed": "ออกจากร้านไม่สำเร็จ",
  "guard.privateMode": "คุณยังอยู่โหมดส่วนตัว · เปิดโหมดคืนนี้ก่อนส่งคำขอ",
  "guard.blockedTable": "คุณบล็อกโต๊ะ {table} ไว้แล้ว",
  "guard.contactOff": "คุณปิดรับคำขอทักทายอยู่ จึงส่งคำขอคอนแทคไม่ได้",
  "guard.contactOffIg": "เปิดรับคำขอทักทายก่อนแนบ IG",
  "guard.duplicateRequest": "มีคำขอถึงโต๊ะ {table} ค้างอยู่แล้ว · รอเขาตอบก่อน",
  "guard.orderActive": "ยังมีออเดอร์ที่ร้านกำลังดูแลอยู่ · ให้พนักงานปิดก่อนออกจากร้าน",
} as const;

export type MessageKey = keyof typeof th;
